<script lang="ts">
  import { get } from 'svelte/store'
  import { tick } from 'svelte'
  import { aiFeaturesStore } from '$lib/stores/aiFeatures'
  import { TextSegmentService } from '$lib/services/TextSegmentService'
  import { AIService } from '$lib/services/AIService'
  import { undoRedoService } from '$lib/services/UndoRedoService'
  import type { AppliedCorrectionInfo } from '$lib/services/UndoRedoService'
  import { settingsStore } from '$lib/stores/settings'
  import { loadingIndicatorService } from '$lib/services/LoadingIndicatorService'
  import { aiServiceActions } from '$lib/stores/aiService'
  
  /**
   * AIFeatureController: Handles all AI-related operations
   * Single Responsibility: AI text processing and split view management
   */
  
  // Store AI analysis service instance
  let aiAnalysisService: any = null
  
  // Utility to maintain scroll position and focus on work area
  function maintainScrollPosition(textarea: HTMLTextAreaElement, targetPosition: number, options?: { 
    ensureVisible?: boolean, 
    centerIfNeeded?: boolean 
  }) {
    if (!textarea) return
    
    const { ensureVisible = true, centerIfNeeded = false } = options || {}
    
    // Calculate the position in the text
    const textBeforePosition = textarea.value.substring(0, targetPosition)
    const lines = textBeforePosition.split('\n')
    const targetLine = lines.length
    
    // Get accurate line height and visible area
    const computedStyle = window.getComputedStyle(textarea)
    const lineHeight = parseFloat(computedStyle.lineHeight) || 
                      parseFloat(computedStyle.fontSize) * 1.5 || 
                      20
    const paddingTop = parseFloat(computedStyle.paddingTop) || 0
    const paddingBottom = parseFloat(computedStyle.paddingBottom) || 0
    const visibleHeight = textarea.clientHeight - paddingTop - paddingBottom
    const visibleLines = Math.floor(visibleHeight / lineHeight)
    
    // Calculate current scroll position in lines
    const currentScrollLines = Math.floor(textarea.scrollTop / lineHeight)
    
    // Determine if we need to scroll
    const targetIsAboveView = targetLine < currentScrollLines + 2 // Keep 2 lines margin at top
    const targetIsBelowView = targetLine > currentScrollLines + visibleLines - 3 // Keep 3 lines margin at bottom
    
    if (ensureVisible && (targetIsAboveView || targetIsBelowView || centerIfNeeded)) {
      let newScrollTop: number
      
      if (centerIfNeeded || (targetIsAboveView && targetIsBelowView)) {
        // Center the target line in the viewport
        newScrollTop = Math.max(0, (targetLine - Math.floor(visibleLines / 2)) * lineHeight)
      } else if (targetIsAboveView) {
        // Scroll up to show the target with margin
        newScrollTop = Math.max(0, (targetLine - 2) * lineHeight)
      } else {
        // Scroll down to show the target with margin
        newScrollTop = Math.max(0, (targetLine - visibleLines + 3) * lineHeight)
      }
      
      // Use instant scrolling for immediate feedback
      textarea.scrollTop = newScrollTop
    }
    
    // Set cursor position for better tracking
    textarea.setSelectionRange(targetPosition, targetPosition)
  }
  
  interface Props {
    editorText: string
    errorState: any
    hasChecked: boolean
    correctionStates: Map<string, AppliedCorrectionInfo>
    lastHistoryText: string | null
    textManager: any
    showSplitEditor?: boolean
    viewMode?: 'source' | 'wysiwyg'
    tipTapEditor?: any
    textareaElement?: HTMLTextAreaElement | null
    inlineViewController?: any
    onStatusMessage: (message: string, duration?: number | 'persist', type?: 'error' | 'info' | 'success') => void
    onSplitViewEnter: (mode: string, segments: any[]) => void
    onSplitViewExit: () => void
    onTextUpdate: (text: string, isManualHistory?: boolean) => void
    onTextReplace?: (start: number, end: number, newText: string) => void
  }
  
  let {
    editorText = $bindable(''),
    errorState,
    hasChecked = $bindable(false),
    correctionStates,
    lastHistoryText,
    textManager,
    showSplitEditor = false,
    viewMode = 'source',
    tipTapEditor,
    textareaElement = null,
    inlineViewController,
    onStatusMessage,
    onSplitViewEnter,
    onSplitViewExit,
    onTextUpdate,
    onTextReplace
  }: Props = $props()
  
  let isAIProcessing = $state(false)
  let aiSegments = $state<any[]>([])
  let hasInitialAIState = $state(false)
  // Loading indicator service handles all indicator management
  
  // Helper function to remove YAML front matter from text
  function removeYamlFrontMatter(text: string): { text: string, hasYaml: boolean, yamlContent: string, yamlOffset: number } {
    const yamlMatch = text.match(/^---[\r\n]+[\s\S]*?[\r\n]+---[\r\n]+/)
    
    if (yamlMatch) {
      return {
        text: text.substring(yamlMatch[0].length),
        hasYaml: true,
        yamlContent: yamlMatch[0],
        yamlOffset: yamlMatch[0].length
      }
    }
    
    return {
      text: text,
      hasYaml: false,
      yamlContent: '',
      yamlOffset: 0
    }
  }
  
  // Get abbreviated model name for display
  function getAbbreviatedModelName(): string {
    const settings = settingsStore.get()
    const modelId = settings.ai?.selectedModel || ''
    
    if (!modelId) return 'AI'
    
    // Common models - return short names
    if (modelId.includes('gpt-4o-mini')) return 'GPT-4o Mini'
    if (modelId.includes('gpt-4o')) return 'GPT-4o'
    if (modelId.includes('claude-3-5-sonnet')) return 'Claude Sonnet'
    if (modelId.includes('claude-3-5-haiku')) return 'Claude Haiku'
    if (modelId.includes('claude-3-opus')) return 'Claude Opus'
    if (modelId.includes('gemini-1.5-pro')) return 'Gemini Pro'
    if (modelId.includes('gemini-1.5-flash')) return 'Gemini Flash'
    if (modelId.includes('solar')) return 'Solar'
    
    // OpenRouter format: take model name after slash
    if (modelId.includes('/')) {
      const parts = modelId.split('/')
      const modelName = parts[parts.length - 1]
      return modelName.split('-').slice(0, 2).join(' ')
    }
    
    return modelId.split('-').slice(0, 2).join(' ')
  }
  
  // Export public methods for parent to call
  export async function handleParaphrase(selectionInfo?: { start: number, end: number, text: string }, isWysiwyg: boolean = false) {

    const textarea = !isWysiwyg ? textareaElement : null

    // Save original text at the very beginning, before any processing
    const originalTextToReplace = editorText

    let textToProcess = editorText
    let selection = selectionInfo || null
    let loadingIndicator: HTMLElement | null = null
    
    // Check for YAML front matter
    const yamlInfo = removeYamlFrontMatter(editorText)
    
    // Check if there's a selection (either passed in or from textarea)
    if (!selection && !isWysiwyg && textarea && textarea.selectionStart !== textarea.selectionEnd) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      
      // If selection is entirely within YAML, don't process
      if (yamlInfo.hasYaml && end <= yamlInfo.yamlOffset) {
        onStatusMessage('YAML 헤더는 AI 처리에서 제외됩니다')
        return
      }
      
      // If selection starts after YAML, use selected text as is
      if (!yamlInfo.hasYaml || start >= yamlInfo.yamlOffset) {
        textToProcess = editorText.substring(start, end)
        selection = { start, end, text: textToProcess }
      } else {
        // Selection spans YAML and content, use only content part
        const adjustedStart = Math.max(start, yamlInfo.yamlOffset)
        textToProcess = editorText.substring(adjustedStart, end)
        selection = { start: adjustedStart, end, text: textToProcess }
      }
    } else if (selection) {
      // Use the passed-in selection
      textToProcess = selection.text
    } else {
      // If no selection, process entire text excluding YAML
      if (!yamlInfo.text || yamlInfo.text.trim().length === 0) {
        onStatusMessage('개선할 텍스트를 입력하거나 선택해주세요')
        return
      }
      textToProcess = yamlInfo.text
    }
    
    // Store selection info
    aiFeaturesStore.setSelection(selection)
    aiFeaturesStore.setMode('paraphrase')
    
    // Scroll to selection if we have one
    if (selection && textarea) {
      requestAnimationFrame(() => {
        maintainScrollPosition(textarea, selection.start, {
          ensureVisible: true,
          centerIfNeeded: false
        })
      })
    }
    
    // Check if we should use split view or inline
    // If selection was passed from parent, always use inline (parent already decided based on length)
    const shouldSplit = selectionInfo ? false : TextSegmentService.shouldSegment(textToProcess)

    if (shouldSplit) {
      // Save state before entering split view
      saveAISegmentsState('AI 모드 시작 전')
      
      // Use split editor for multi-segment text
      const initialSegments = TextSegmentService.segmentText(textToProcess)
      aiSegments = initialSegments
      aiFeaturesStore.setSegments(initialSegments)
      aiFeaturesStore.enterSplitView(textToProcess, '')
      
      // Reset initial state flag when starting new AI session
      hasInitialAIState = false
      
      // Set AI processing flag for persistent status
      isAIProcessing = true
      const modelName = getAbbreviatedModelName()
      onStatusMessage(`개선 및 변경하기 모드를 시작합니다... (${modelName})`, 'persist')
      onSplitViewEnter('paraphrase', initialSegments)
    } else {
      // For single segment, process inline
      // Show loading at end of selection or text (only for source mode with textarea)
      if (!isWysiwyg && textarea) {
        if (selection) {
          loadingIndicator = loadingIndicatorService.showLoadingIndicator(textarea, selection.end, 'paraphrase')
        } else {
          loadingIndicator = loadingIndicatorService.showLoadingIndicator(textarea, editorText.length, 'paraphrase')
        }
      }
      
      isAIProcessing = true
      const modelName = getAbbreviatedModelName()
      const settings = settingsStore.get()
      const modelId = settings.ai?.selectedModel
      
      onStatusMessage(`텍스트를 개선하고 있습니다... (${modelName})`, 'persist')
      
      try {
        aiFeaturesStore.setProcessing(true)
        
        // Use streaming for inline processing
        let accumulatedText = ''
        let cursorHidden = false

        const response = await AIService.paraphraseStreaming(
          textToProcess,
          (chunk) => {
            // Accumulate chunks
            accumulatedText += chunk
            
            // Update text in real-time
            if (isWysiwyg && tipTapEditor) {
              // For WYSIWYG mode, update TipTap editor directly
              const editor = (tipTapEditor as any).editor
              if (editor) {
                // Hide cursor during streaming if not already hidden
                if (!cursorHidden) {
                  editor.commands.blur()
                  cursorHidden = true
                }
                
                if (selection) {
                  // Not implemented for selection in WYSIWYG streaming yet
                  // Just accumulate for final update
                } else {
                  // Replace entire content
                  editor.commands.setContent(accumulatedText)
                }
              }
            } else {
              // For source mode, use original text as base
              if (selection) {
                const newText = originalTextToReplace.substring(0, selection.start) + 
                              accumulatedText + 
                              originalTextToReplace.substring(selection.end)
                onTextUpdate(newText, false) // false = don't add to history yet
              } else {
                onTextUpdate(accumulatedText, false) // false = don't add to history yet
              }
            }
          },
          modelId
        )
        
        // Check if the response indicates rate limit exceeded
        if (response && !response.success && response.error &&
            (response.error.includes('한도') || response.error.includes('limit'))) {
          // Rate limit exceeded - upgrade modal is already triggered by AIService
          // Restore original text without showing error message
          onTextUpdate(originalTextToReplace, false)
          isAIProcessing = false
          aiFeaturesStore.setProcessing(false)
          aiFeaturesStore.reset()
          aiServiceActions.cancel() // Cancel any ongoing AI analysis
          return
        }

        if (response.success && response.result) {
          // Final update with history
          if (isWysiwyg && tipTapEditor) {
            // For WYSIWYG mode, update TipTap editor
            const editor = (tipTapEditor as any).editor
            if (editor) {
              if (selection) {
                // Not fully implemented for selection in WYSIWYG yet
                const newText = editorText.substring(0, selection.start) + 
                              response.result + 
                              editorText.substring(selection.end)
                editor.commands.setContent(newText)
                onTextUpdate(newText, true)
              } else {
                // Replace entire content
                editor.commands.setContent(response.result)
                onTextUpdate(response.result as string, true)
              }
              // Restore cursor after completion
              editor.commands.focus('end')
            }
          } else {
            // For source mode
            if (selection) {
              const newText = editorText.substring(0, selection.start) + 
                            response.result + 
                            editorText.substring(selection.end)
              
              // Add to undo history
              undoRedoService.addEntry('ai-paraphrase', newText, correctionStates, {
                description: '개선 및 변경하기 적용',
                previousText: originalTextToReplace
              }, errorState.errors, hasChecked, errorState.ignoredErrors)
              
              onTextUpdate(newText, true)
            } else {
              // Replace entire text
              undoRedoService.addEntry('ai-paraphrase', response.result as string, correctionStates, {
                description: '개선 및 변경하기 전체 적용',
                previousText: originalTextToReplace
              }, errorState.errors, hasChecked, errorState.ignoredErrors)
              
              onTextUpdate(response.result as string, true)
            }
          }
          
          onStatusMessage('텍스트가 개선되었습니다')
        } else {
          // Restore original text on failure
          onTextUpdate(originalTextToReplace, false)
          onStatusMessage('개선 작업이 실패했습니다. 다시 시도해주세요')
        }
      } catch (error) {
        console.error('Paraphrase error:', error)
        onStatusMessage('개선 작업 중 오류가 발생했습니다')
      } finally {
        loadingIndicatorService.hideLoadingIndicator('paraphrase')
        isAIProcessing = false
        aiFeaturesStore.setProcessing(false)
        aiFeaturesStore.reset()
      }
    }
  }
  
  export async function handleExpand(selectionInfo?: { start: number, end: number, text: string }, isWysiwyg: boolean = false) {
    const textarea = !isWysiwyg ? textareaElement : null

    // Save original text at the very beginning
    const originalTextToReplace = editorText

    let textToProcess = editorText
    let selection = selectionInfo || null
    let loadingIndicator: HTMLElement | null = null
    
    // Check for YAML front matter
    const yamlInfo = removeYamlFrontMatter(editorText)
    
    // Check if there's a selection (either passed in or from textarea)
    if (!selection && textarea && textarea.selectionStart !== textarea.selectionEnd) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      
      // If selection is entirely within YAML, don't process
      if (yamlInfo.hasYaml && end <= yamlInfo.yamlOffset) {
        onStatusMessage('YAML 헤더는 AI 처리에서 제외됩니다')
        return
      }
      
      // If selection starts after YAML, use selected text as is
      if (!yamlInfo.hasYaml || start >= yamlInfo.yamlOffset) {
        textToProcess = editorText.substring(start, end)
        selection = { start, end, text: textToProcess }
      } else {
        // Selection spans YAML and content, use only content part
        const adjustedStart = Math.max(start, yamlInfo.yamlOffset)
        textToProcess = editorText.substring(adjustedStart, end)
        selection = { start: adjustedStart, end, text: textToProcess }
      }
    } else if (selection) {
      // Use the passed-in selection
      textToProcess = selection.text
    } else {
      // If no selection, process entire text excluding YAML
      if (!yamlInfo.text || yamlInfo.text.trim().length === 0) {
        onStatusMessage('내용을 늘릴 텍스트를 입력하거나 선택해주세요')
        return
      }
      textToProcess = yamlInfo.text
    }
    
    // Store selection info
    aiFeaturesStore.setSelection(selection)
    aiFeaturesStore.setMode('expand')
    
    // Check if we should use split view
    // If selection was passed from parent, always use inline (parent already decided based on length)
    const shouldSplit = selectionInfo ? false : TextSegmentService.shouldSegment(textToProcess)
    
    if (shouldSplit) {
      // Save state before entering split view
      saveAISegmentsState('AI 늘리기 모드 시작 전')
      
      // Initialize segments for split view
      const initialSegments = TextSegmentService.segmentText(textToProcess)
      aiSegments = initialSegments
      aiFeaturesStore.setSegments(initialSegments)
      aiFeaturesStore.enterSplitView(textToProcess, '')
      
      // Reset initial state flag when starting new AI session
      hasInitialAIState = false
      
      isAIProcessing = true
      onStatusMessage('내용 늘리기 모드를 시작합니다...', 'persist')
      onSplitViewEnter('expand', initialSegments)
    } else {
      // Show loading at end of selection or text (only for source mode)
      if (!isWysiwyg && textarea) {
        if (selection) {
          loadingIndicator = loadingIndicatorService.showLoadingIndicator(textarea, selection.end, 'expand')
        } else {
          loadingIndicator = loadingIndicatorService.showLoadingIndicator(textarea, editorText.length, 'expand')
        }
      }
      
      isAIProcessing = true
      const modelName = getAbbreviatedModelName()
      const settings = settingsStore.get()
      const modelId = settings.ai?.selectedModel
      
      onStatusMessage(`내용을 확장하고 있습니다... (${modelName})`, 'persist')
      
      try {
        aiFeaturesStore.setProcessing(true)
        
        // Use streaming for inline processing
        let accumulatedText = ''
        let cursorHidden = false

        const response = await AIService.expandStreaming(
          textToProcess,
          (chunk) => {
            // Accumulate chunks
            accumulatedText += chunk
            
            // Update text in real-time
            if (isWysiwyg && tipTapEditor) {
              // For WYSIWYG mode, update TipTap editor directly
              const editor = (tipTapEditor as any).editor
              if (editor) {
                // Hide cursor during streaming if not already hidden
                if (!cursorHidden) {
                  editor.commands.blur()
                  cursorHidden = true
                }
                
                if (selection) {
                  // Not implemented for selection in WYSIWYG streaming yet
                } else {
                  // Replace entire content
                  editor.commands.setContent(accumulatedText)
                }
              }
            } else {
              // For source mode, use original text as base
              if (selection) {
                const newText = originalTextToReplace.substring(0, selection.start) + 
                              accumulatedText + 
                              originalTextToReplace.substring(selection.end)
                onTextUpdate(newText, false) // false = don't add to history yet
              } else {
                onTextUpdate(accumulatedText, false) // false = don't add to history yet
              }
            }
          },
          modelId
        )
        
        // Check if the response indicates rate limit exceeded
        if (response && !response.success && response.error &&
            (response.error.includes('한도') || response.error.includes('limit'))) {
          // Rate limit exceeded - upgrade modal is already triggered by AIService
          // Restore original text without showing error message
          onTextUpdate(originalTextToReplace, false)
          isAIProcessing = false
          aiFeaturesStore.setProcessing(false)
          aiFeaturesStore.reset()
          aiServiceActions.cancel() // Cancel any ongoing AI analysis
          return
        }

        if (response.success && response.result) {
          // Final update with history
          if (isWysiwyg && tipTapEditor) {
            // For WYSIWYG mode, update TipTap editor
            const editor = (tipTapEditor as any).editor
            if (editor) {
              if (selection) {
                const newText = editorText.substring(0, selection.start) + 
                              response.result + 
                              editorText.substring(selection.end)
                editor.commands.setContent(newText)
                onTextUpdate(newText, true)
              } else {
                editor.commands.setContent(response.result)
                onTextUpdate(response.result as string, true)
              }
              // Restore cursor after completion
              editor.commands.focus('end')
            }
          } else {
            // For source mode
            const newText = selection 
              ? editorText.substring(0, selection.start) + 
                response.result + 
                editorText.substring(selection.end)
              : response.result as string
            
            undoRedoService.addEntry('ai-expand', newText, correctionStates, {
              description: '내용 늘리기 적용',
              previousText: originalTextToReplace
            }, errorState.errors, hasChecked, errorState.ignoredErrors)
            
            onTextUpdate(newText, true)
          }
          onStatusMessage('내용이 확장되었습니다')
        } else {
          // Restore original text on failure
          onTextUpdate(originalTextToReplace, false)
          onStatusMessage('내용 확장에 실패했습니다')
        }
      } finally {
        loadingIndicatorService.hideLoadingIndicator('expand')
        isAIProcessing = false
        aiFeaturesStore.setProcessing(false)
        aiFeaturesStore.reset()
      }
    }
  }
  
  export async function handleShrink(selectionInfo?: { start: number, end: number, text: string }, isWysiwyg: boolean = false) {
    const textarea = !isWysiwyg ? textareaElement : null

    // Save original text at the very beginning
    const originalTextToReplace = editorText

    let textToProcess = editorText
    let selection = selectionInfo || null
    let loadingIndicator: HTMLElement | null = null
    
    // Check for YAML front matter
    const yamlInfo = removeYamlFrontMatter(editorText)
    
    // Check if there's a selection (either passed in or from textarea)
    if (!selection && textarea && textarea.selectionStart !== textarea.selectionEnd) {
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      
      // If selection is entirely within YAML, don't process
      if (yamlInfo.hasYaml && end <= yamlInfo.yamlOffset) {
        onStatusMessage('YAML 헤더는 AI 처리에서 제외됩니다')
        return
      }
      
      // If selection starts after YAML, use selected text as is
      if (!yamlInfo.hasYaml || start >= yamlInfo.yamlOffset) {
        textToProcess = editorText.substring(start, end)
        selection = { start, end, text: textToProcess }
      } else {
        // Selection spans YAML and content, use only content part
        const adjustedStart = Math.max(start, yamlInfo.yamlOffset)
        textToProcess = editorText.substring(adjustedStart, end)
        selection = { start: adjustedStart, end, text: textToProcess }
      }
    } else if (selection) {
      // Use the passed-in selection
      textToProcess = selection.text
    } else {
      // If no selection, process entire text excluding YAML
      if (!yamlInfo.text || yamlInfo.text.trim().length === 0) {
        onStatusMessage('내용을 줄일 텍스트를 입력하거나 선택해주세요')
        return
      }
      textToProcess = yamlInfo.text
    }
    
    // Store selection info
    aiFeaturesStore.setSelection(selection)
    aiFeaturesStore.setMode('shrink')
    
    // Check if we should use split view
    // If selection was passed from parent, always use inline (parent already decided based on length)
    const shouldSplit = selectionInfo ? false : TextSegmentService.shouldSegment(textToProcess)
    
    if (shouldSplit) {
      // Save state before entering split view
      saveAISegmentsState('AI 줄이기 모드 시작 전')
      
      // Initialize segments for split view
      const initialSegments = TextSegmentService.segmentText(textToProcess)
      aiSegments = initialSegments
      aiFeaturesStore.setSegments(initialSegments)
      aiFeaturesStore.enterSplitView(textToProcess, '')
      
      // Reset initial state flag when starting new AI session
      hasInitialAIState = false
      
      isAIProcessing = true
      const modelName = getAbbreviatedModelName()
      onStatusMessage(`내용 줄이기 모드를 시작합니다... (${modelName})`, 'persist')
      onSplitViewEnter('shrink', initialSegments)
    } else {
      // Show loading at end of selection or text (only for source mode)
      if (!isWysiwyg && textarea) {
        if (selection) {
          loadingIndicator = loadingIndicatorService.showLoadingIndicator(textarea, selection.end, 'shrink')
        } else {
          loadingIndicator = loadingIndicatorService.showLoadingIndicator(textarea, editorText.length, 'shrink')
        }
      }
      
      isAIProcessing = true
      const modelName = getAbbreviatedModelName()
      const settings = settingsStore.get()
      const modelId = settings.ai?.selectedModel
      
      onStatusMessage(`내용을 간결하게 만들고 있습니다... (${modelName})`, 'persist')
      
      try {
        aiFeaturesStore.setProcessing(true)
        
        // Use streaming for inline processing
        let accumulatedText = ''
        let cursorHidden = false

        const response = await AIService.shrinkStreaming(
          textToProcess,
          (chunk) => {
            // Accumulate chunks
            accumulatedText += chunk
            
            // Update text in real-time
            if (isWysiwyg && tipTapEditor) {
              // For WYSIWYG mode, update TipTap editor directly
              const editor = (tipTapEditor as any).editor
              if (editor) {
                // Hide cursor during streaming if not already hidden
                if (!cursorHidden) {
                  editor.commands.blur()
                  cursorHidden = true
                }
                
                if (selection) {
                  // Not implemented for selection in WYSIWYG streaming yet
                } else {
                  // Replace entire content
                  editor.commands.setContent(accumulatedText)
                }
              }
            } else {
              // For source mode, use original text as base
              if (selection) {
                const newText = originalTextToReplace.substring(0, selection.start) + 
                              accumulatedText + 
                              originalTextToReplace.substring(selection.end)
                onTextUpdate(newText, false) // false = don't add to history yet
              } else {
                onTextUpdate(accumulatedText, false) // false = don't add to history yet
              }
            }
          },
          modelId
        )
        
        // Check if the response indicates rate limit exceeded
        if (response && !response.success && response.error &&
            (response.error.includes('한도') || response.error.includes('limit'))) {
          // Rate limit exceeded - upgrade modal is already triggered by AIService
          // Restore original text without showing error message
          onTextUpdate(originalTextToReplace, false)
          isAIProcessing = false
          aiFeaturesStore.setProcessing(false)
          aiFeaturesStore.reset()
          aiServiceActions.cancel() // Cancel any ongoing AI analysis
          return
        }

        if (response.success && response.result) {
          // Final update with history
          if (isWysiwyg && tipTapEditor) {
            // For WYSIWYG mode, update TipTap editor
            const editor = (tipTapEditor as any).editor
            if (editor) {
              if (selection) {
                const newText = editorText.substring(0, selection.start) + 
                              response.result + 
                              editorText.substring(selection.end)
                editor.commands.setContent(newText)
                onTextUpdate(newText, true)
              } else {
                editor.commands.setContent(response.result)
                onTextUpdate(response.result as string, true)
              }
              // Restore cursor after completion
              editor.commands.focus('end')
            }
          } else {
            // For source mode
            const newText = selection 
              ? editorText.substring(0, selection.start) + 
                response.result + 
                editorText.substring(selection.end)
              : response.result as string
            
            undoRedoService.addEntry('ai-shrink', newText, correctionStates, {
              description: '내용 줄이기 적용',
              previousText: originalTextToReplace
            }, errorState.errors, hasChecked, errorState.ignoredErrors)
            
            onTextUpdate(newText, true)
          }
          onStatusMessage('내용이 간결해졌습니다')
        } else {
          // Restore original text on failure
          onTextUpdate(originalTextToReplace, false)
          onStatusMessage('내용 축소에 실패했습니다')
        }
      } finally {
        loadingIndicatorService.hideLoadingIndicator('shrink')
        isAIProcessing = false
        aiFeaturesStore.setProcessing(false)
        aiFeaturesStore.reset()
      }
    }
  }
  
  export async function handleContinue(mode: 'sentence' | 'paragraph' = 'sentence') {
    // Save original text at the very beginning
    const originalTextToReplace = editorText

    // For WYSIWYG mode, use InlineViewController's processGeneration method
    // which handles streaming better without the first character issue
    if (viewMode === 'wysiwyg' && tipTapEditor) {
      const editor = (tipTapEditor as any).editor
      if (!editor) {
        onStatusMessage('텍스트 편집기를 찾을 수 없습니다', 3500, 'error')
        return
      }

      const { from, to } = editor.state.selection

      // Get text before and after cursor using ProseMirror's textBetween
      // This uses TipTap's native coordinate system
      const doc = editor.state.doc
      const docSize = doc.content.size

      // Get text before cursor (up to 2000 chars)
      const beforeStart = Math.max(0, from - 2000)
      const beforeCursor = doc.textBetween(beforeStart, from, '\n\n')

      // Build context-aware prompt with position information
      const contextLength = beforeCursor.length
      const positionPercent = Math.round((from / docSize) * 100)

      const contextNote = contextLength > 0 && contextLength < 2000
        ? `\n\n[참고: 이것은 문서의 마지막 ${contextLength}자입니다. 현재 위치는 전체 문서의 약 ${positionPercent}% 지점입니다.]`
        : ''

      // Create a continuation prompt based on context
      const continuationPrompt = mode === 'paragraph'
        ? `다음 내용을 이어서 한 문단을 작성해주세요:${contextNote}\n\n${beforeCursor}`
        : `다음 내용을 이어서 1-2문장을 작성해주세요:${contextNote}\n\n${beforeCursor}`

      // Call AI generation service with streaming
      const settings = get(settingsStore)
      const modelId = settings.ai?.selectedModel

      // Show loading indicator at cursor position
      let loadingIndicator: HTMLElement | null = null
      const coords = editor.view.coordsAtPos(from)

      // Create loading indicator element
      loadingIndicator = document.createElement('div')
      loadingIndicator.className = 'continuation-loading-indicator'
      loadingIndicator.style.cssText = `
        position: fixed;
        left: ${coords.left}px;
        top: ${coords.top}px;
        z-index: 10000;
        pointer-events: none;
      `
      loadingIndicator.innerHTML = `
        <div class="loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      `
      document.body.appendChild(loadingIndicator)
      ;(loadingIndicator as any).type = 'wysiwyg'

      onStatusMessage?.('텍스트 생성 중...', 'persist')

      try {
        let streamedText = ''
        let isFirstChunk = true

        const response = await AIService.generateStreaming(
          continuationPrompt,
          {
            beforeText: beforeCursor,
            // Note: afterText is intentionally not passed - we want to generate
            // continuation based only on what came before the cursor
            cursorPosition: from,
            mode,
            modelId
          },
          (chunk: string) => {
            if (isFirstChunk) {
              // Hide loading indicator on first chunk
              if (loadingIndicator) {
                loadingIndicator.remove()
                loadingIndicator = null
              }
              isFirstChunk = false
            }
            streamedText += chunk

            // Insert text directly into TipTap
            if (editor) {
              editor.commands.insertContent(chunk)
            }
          }
        )

        // Hide loading indicator if still showing
        if (loadingIndicator) {
          loadingIndicator.remove()
          loadingIndicator = null
        }

        if (response?.success && (streamedText || response.result)) {
          onStatusMessage?.('텍스트가 생성되었습니다')
        } else {
          onStatusMessage?.('텍스트 생성에 실패했습니다', 3500, 'error')
        }
      } catch (error) {
        // Hide loading indicator on error
        if (loadingIndicator) {
          loadingIndicator.remove()
          loadingIndicator = null
        }
        console.error('[AIFeatureController] Generation error:', error)
        onStatusMessage?.('텍스트 생성 중 오류가 발생했습니다', 3500, 'error')
      }

      return
    }

    // Original implementation for source mode
    let cursorPosition = 0
    let hasSelection = false
    let selectionStart = 0
    let selectionEnd = 0
    let textToProcess = editorText
    let processMode: 'inline' | 'splitview' = 'inline'
    let textarea: HTMLTextAreaElement | null = null
    let yamlOffset = 0 // Store YAML offset for later use

    // Source mode - use bound textareaElement
    textarea = textareaElement
    if (!textarea) {
      console.error('[AIFeatureController] handleContinue: No textarea found')
      onStatusMessage('텍스트 편집기를 찾을 수 없습니다', 3500, 'error')
      return
    }
    
    cursorPosition = textarea.selectionStart
    hasSelection = textarea.selectionStart !== textarea.selectionEnd
    selectionStart = textarea.selectionStart
    selectionEnd = textarea.selectionEnd
    
    // Maintain scroll position at cursor/selection
    requestAnimationFrame(() => {
      maintainScrollPosition(textarea, cursorPosition, {
        ensureVisible: true,
        centerIfNeeded: false
      })
    })
    
    // Check if there's a large selection for split view
    if (hasSelection) {
      const selectedText = editorText.substring(selectionStart, selectionEnd)
      
      // If selection is more than 100 characters or contains paragraph breaks, use split view
      if (selectedText.length > 100 || selectedText.includes('\n\n')) {
        processMode = 'splitview'
        textToProcess = selectedText
      }
    }

    if (processMode === 'splitview') {
      // Process in split view for large selections
      isAIProcessing = true
      const modelName = getAbbreviatedModelName()
      onStatusMessage(`${mode === 'paragraph' ? '문단' : '문장'} 이어쓰기 중... (${modelName})`, 'persist')
      
      // Segment the text
      const segments = TextSegmentService.segmentTextV2(textToProcess)
      
      // Continue each segment
      try {
        const continuedSegments = await Promise.all(
          segments.map(async (segment) => {
            const response = await AIService.continueWriting(segment.original, {
              mode,
              context: {
                position: `Segment ${segment.id}`,
                documentType: 'general'
              }
            })
            
            // Check if the response indicates rate limit exceeded
        if (response && !response.success && response.error &&
            (response.error.includes('한도') || response.error.includes('limit'))) {
          // Rate limit exceeded - upgrade modal is already triggered by AIService
          // Restore original text without showing error message
          onTextUpdate(originalTextToReplace, false)
          isAIProcessing = false
          aiFeaturesStore.setProcessing(false)
          aiFeaturesStore.reset()
          aiServiceActions.cancel() // Cancel any ongoing AI analysis
          return
        }

        if (response.success && response.result) {
              return {
                ...segment,
                suggested: segment.original + ' ' + response.result,
                reason: `${mode === 'paragraph' ? '문단' : '문장'}을 이어서 작성했습니다`,
                hasChanges: true,
                mode: 'continue' as const
              }
            }
            return segment
          })
        )
        
        // Enter split view with continued segments
        aiSegments = continuedSegments
        aiFeaturesStore.enterSplitView({
          text: textToProcess,
          mode: 'continue',
          selection: {
            text: textToProcess,
            start: selectionStart,
            end: selectionEnd
          }
        })
        
        onSplitViewEnter('continue', continuedSegments)
        isAIProcessing = false
        onStatusMessage(`${mode === 'paragraph' ? '문단' : '문장'} 이어쓰기 완료`)
      } catch (error) {
        console.error('Continue error:', error)
        isAIProcessing = false
        onStatusMessage('이어쓰기 중 오류가 발생했습니다', 3500, 'error')
      } finally {
        isAIProcessing = false
      }
    } else {
      // Inline continuation for cursor position or small selection

      // Check if there's a small selection (< 100 chars)
      let selectedText = ''
      let contextText = ''
      
      if (hasSelection) {
        // Small selection: use selected text as context and append continuation
        selectedText = editorText.substring(selectionStart, selectionEnd)
        contextText = selectedText
        // Move cursor to end of selection for continuation
        cursorPosition = selectionEnd
      }
      
      // Show loading indicator at cursor/end of selection
      let loadingCursorPos = cursorPosition
      let indicator: any = null
      let loadingInserted = false
      
      if (viewMode === 'wysiwyg' && tipTapEditor) {
        // For WYSIWYG mode, insert a loading indicator at cursor
        const editor = (tipTapEditor as any).editor
        if (editor) {
          // Don't insert loading indicator in WYSIWYG to avoid undo issues
          // Just show status message
          loadingInserted = false
        }
        onStatusMessage('텍스트 생성 중...', 'persist')
      } else if (textarea) {
        const loadingResult = loadingIndicatorService.showContinuationLoading(textarea)
        loadingCursorPos = loadingResult.cursorPos
        indicator = loadingResult.indicator
      }

      isAIProcessing = true
      const modelName = getAbbreviatedModelName()
      const settings = settingsStore.get()
      const modelId = settings.ai?.selectedModel
      onStatusMessage(`${mode === 'paragraph' ? '문단' : '문장'} 이어쓰기 중... (${modelName})`, 'persist')

      // Save original text before any modifications
      const originalText = editorText
      
      try {
        // Get context - use selected text if available, otherwise text before cursor
        // In WYSIWYG mode, we need to use the actual text position without YAML
        let contextPosition = cursorPosition
        let contextText = originalText
        
        // If in WYSIWYG mode, extract context from text without YAML
        if (viewMode === 'wysiwyg' && yamlOffset > 0) {
          // Remove YAML from text for context extraction
          const yamlMatch = originalText.match(/^---\n[\s\S]*?\n---\n/)
          if (yamlMatch) {
            contextText = originalText.substring(yamlOffset)
            // Adjust position to be relative to text without YAML
            contextPosition = cursorPosition - yamlOffset
          }
        }
        
        // Get more context for better continuation (2000 chars instead of 500)
        const beforeCursor = hasSelection
          ? selectedText
          : contextText.substring(Math.max(0, contextPosition - 2000), contextPosition)
        
        
        // Analyze previous paragraphs for pattern (use contextText for WYSIWYG mode)
        const previousText = contextText.substring(0, contextPosition)
        const paragraphs = previousText.split(/\n\n+/).filter(p => p.trim())
        
        // Calculate average paragraph length and sentence count
        let avgParagraphLength = 0
        let avgSentenceCount = 0
        let avgSentenceLength = 0
        
        if (paragraphs.length > 0) {
          // Analyze last 3-5 paragraphs for pattern
          const recentParagraphs = paragraphs.slice(-5)
          const paragraphLengths = recentParagraphs.map(p => p.length)
          const sentenceCounts = recentParagraphs.map(p => {
            const sentences = p.split(/[.!?]+/).filter(s => s.trim())
            return sentences.length
          })
          
          // Calculate average sentence length too
          const allSentences = recentParagraphs.flatMap(p => 
            p.split(/[.!?]+/).filter(s => s.trim())
          )
          
          if (allSentences.length > 0) {
            const sentenceLengths = allSentences.map(s => s.length)
            avgSentenceLength = Math.round(sentenceLengths.reduce((a, b) => a + b, 0) / sentenceLengths.length)
          }
          
          avgParagraphLength = Math.round(paragraphLengths.reduce((a, b) => a + b, 0) / paragraphLengths.length)
          avgSentenceCount = Math.round(sentenceCounts.reduce((a, b) => a + b, 0) / sentenceCounts.length)
        }
        
        // Determine context information (use contextText for proper position calculation)
        const lines = contextText.substring(0, contextPosition).split('\n')
        const currentLine = lines.length
        const totalLines = contextText.split('\n').length
        const relativePosition = totalLines > 0 ? `${Math.round((currentLine / totalLines) * 100)}%` : 'beginning'
        
        // Find section title (look for heading)
        let sectionTitle = ''
        for (let i = lines.length - 1; i >= 0 && i > lines.length - 10; i--) {
          if (lines[i].match(/^#+\s+(.+)$/)) {
            sectionTitle = lines[i].replace(/^#+\s+/, '')
            break
          }
        }
        
        // Prepare for streaming
        let streamedText = ''
        // Check if we need a space before continuation
        // Use selection end if there was a selection, otherwise cursor position
        const checkPos = hasSelection ? selectionEnd : cursorPosition
        const needSpace = checkPos > 0 && originalText[checkPos - 1] && !originalText[checkPos - 1].match(/[\s\n]/)
        const prefix = originalText.substring(0, cursorPosition) + (needSpace ? ' ' : '')
        const suffix = originalText.substring(cursorPosition)
        
        // Track state for streaming
        let spaceAdded = false
        let isFirstChunk = true
        let chunkCount = 0
        
        // Streaming callback to update text in real-time
        const onChunk = (chunk: string) => {
          chunkCount++
          
          // Remove leading newlines from the first chunk (only newlines, not other characters)
          if (isFirstChunk) {
            chunk = chunk.replace(/^[\n\r]+/, '')
            
            // Remove loading indicator on first chunk (only for source mode)
            if (indicator && viewMode !== 'wysiwyg') {
              loadingIndicatorService.hideContinuationLoading(indicator)
              indicator = null
            }
            
            isFirstChunk = false
          }
          
          // Skip empty chunks before accumulating
          if (!chunk) return
          
          streamedText += chunk
          
          // Maintain scroll position to keep generation visible
          if (viewMode === 'wysiwyg' && tipTapEditor) {
            // For WYSIWYG mode, directly use TipTap editor
            const editor = (tipTapEditor as any).editor
            if (editor) {
              // First chunk - add space if needed
              if (!spaceAdded && needSpace) {
                editor.commands.insertContent(' ')
                spaceAdded = true
              }
              
              // Insert the chunk directly at current cursor
              editor.commands.insertContent(chunk)
            }
          } else {
            // For source mode, update the entire text
            // Calculate the new text with streamed content
            // If there was a selection, keep it and append continuation after it
            const newText = hasSelection
              ? originalText.substring(0, selectionEnd) + (needSpace ? ' ' : '') + streamedText + originalText.substring(selectionEnd)
              : prefix + streamedText + suffix
            
            // Update text without saving to history during streaming
            onTextUpdate(newText, false)  // false = don't add to history yet
            
            if (textarea) {
              // Calculate new cursor position
              const newCursorPos = cursorPosition + (needSpace ? 1 : 0) + streamedText.length
              
              // Use requestAnimationFrame for proper timing
              requestAnimationFrame(() => {
                if (textarea) {
                  textarea.setSelectionRange(newCursorPos, newCursorPos)
                  
                  // Use the maintainScrollPosition utility instead of scrolling to bottom
                  maintainScrollPosition(textarea, newCursorPos, {
                    ensureVisible: true,
                    centerIfNeeded: false
                  })
                }
              })
            }
          }
        }

        const startTime = Date.now()
        const response = await AIService.continueWritingStreaming(beforeCursor, onChunk, {
          cursorPosition: beforeCursor.length,
          mode,
          modelId,
          context: {
            position: `Line ${currentLine} of ${totalLines}`,
            relativePosition,
            sectionTitle: sectionTitle || undefined,
            documentType: 'general',
            paragraphPattern: avgParagraphLength > 0 || avgSentenceLength > 0 ? {
              avgLength: avgParagraphLength,
              avgSentences: avgSentenceCount,
              avgSentenceLength: avgSentenceLength,
              targetLength: mode === 'paragraph' ? Math.min(Math.max(avgParagraphLength, 100), 300) : undefined,
              targetSentences: mode === 'paragraph' ? Math.min(Math.max(avgSentenceCount, 2), 7) : undefined,
              sentenceStyle: avgSentenceLength < 30 ? 'short' : avgSentenceLength > 60 ? 'long' : 'medium'
            } : undefined
          }
        })

        // Check if we have content from either streaming or response
        const generatedContent = streamedText || response.result
        
        if ((response.success && response.result) || streamedText) {
          // Remove loading indicator
          loadingIndicatorService.hideContinuationLoading(indicator)
          
          // Final text with complete result
          // If there was a selection, keep the selected text and append continuation
          const finalText = hasSelection
            ? originalText.substring(0, selectionEnd) + (needSpace ? ' ' : '') + generatedContent + originalText.substring(selectionEnd)
            : prefix + generatedContent + suffix
          
          // Add to undo history with final text
          undoRedoService.addEntry('ai-continue', finalText, correctionStates, {
            description: `${mode === 'paragraph' ? '문단' : '문장'} 이어쓰기`,
            previousText: originalText
          }, errorState.errors, hasChecked, errorState.ignoredErrors)
          
          // Note: Text has already been updated via streaming in onChunk callback
          // For WYSIWYG mode, text was updated via onTextReplace, so skip final update
          // For source mode, finalize the update with history flag
          if (viewMode !== 'wysiwyg') {
            onTextUpdate(finalText, true, {
              type: 'insertion',
              position: cursorPosition,
              insertedLength: generatedContent.length + (needSpace ? 1 : 0)
            })

            // Set cursor position and focus after completion
            // Use tick() to ensure Svelte's DOM updates are complete
            if (textarea) {
              const finalCursorPos = cursorPosition + (needSpace ? 1 : 0) + generatedContent.length
              tick().then(() => {
                if (textarea) {
                  // Restore cursor visibility (LoadingIndicatorService may have hidden it)
                  textarea.style.caretColor = ''
                  textarea.setSelectionRange(finalCursorPos, finalCursorPos)
                  textarea.focus()
                }
              })
            }
          }

          isAIProcessing = false
          onStatusMessage(`${mode === 'paragraph' ? '문단' : '문장'} 이어쓰기 완료`)
        } else {
          // Remove loading indicator on failure
          loadingIndicatorService.hideContinuationLoading(indicator)
          // Restore cursor visibility
          if (textarea) {
            textarea.style.caretColor = ''
          }
          isAIProcessing = false
          onStatusMessage('이어쓰기를 생성할 수 없습니다', 3500, 'error')
        }
      } catch (error) {
        // Remove loading indicator on error
        loadingIndicatorService.hideContinuationLoading(indicator)
        // Restore cursor visibility
        if (textarea) {
          textarea.style.caretColor = ''
        }

        // Debug: Log error at AIFeatureController level
        console.error('[AIFeatureController] handleContinue error:', {
          error: error instanceof Error ? error.message : 'Unknown error',
          errorStack: error instanceof Error ? error.stack : undefined,
          mode,
          beforeCursorLength: beforeCursor?.length || 0,
          hasSelection,
          streamedTextLength: streamedText?.length || 0
        })

        isAIProcessing = false
        onStatusMessage('이어쓰기 중 오류가 발생했습니다', 3500, 'error')
      } finally {
        isAIProcessing = false
      }
    }
  }
  
  export async function handleSummarize(isWysiwyg = false) {
    // Get fresh textarea reference each time
    const getTextarea = () => {
      if (isWysiwyg) return null
      // Use the bound textareaElement passed from MainEditor
      if (textareaElement) {
        return textareaElement
      }
      
      // Fallback: try to find any textarea (shouldn't reach here normally)
      const allTextareas = document.querySelectorAll('textarea')
      for (const ta of allTextareas) {
        const textarea = ta as HTMLTextAreaElement
        if (!textarea.classList.contains('title-input') && 
            !textarea.classList.contains('prompt-textarea') &&
            !textarea.classList.contains('system-prompt-textarea')) {
          return textarea
        }
      }
      
      return null
    }
    const textarea = getTextarea()
    
    // Helper function to scroll textarea to bottom with multiple attempts
    const scrollToBottom = (textarea: HTMLTextAreaElement, attempts = 0) => {
      if (!textarea || attempts > 5) {
        return
      }
      
      // Only attempt scroll if there's actually content to scroll
      if (textarea.scrollHeight <= textarea.clientHeight) {
        return
      }
      
      // Method 1: Force focus first
      textarea.focus()
      
      // Method 2: Set scroll to max
      const targetScroll = textarea.scrollHeight - textarea.clientHeight
      textarea.scrollTop = targetScroll
      
      // Method 3: Also try scrolling parent containers
      let parent = textarea.parentElement
      while (parent && attempts === 0) {
        if (parent.scrollHeight > parent.clientHeight) {
          parent.scrollTop = parent.scrollHeight
        }
        parent = parent.parentElement
      }
      
      // Method 4: Retry with delay if not successful
      setTimeout(() => {
        const currentScroll = textarea.scrollTop
        const maxScroll = textarea.scrollHeight - textarea.clientHeight
        
        if (currentScroll < maxScroll - 10) {
          scrollToBottom(textarea, attempts + 1)
        }
      }, 100)
    }
    let textToSummarize = editorText
    let selection = null
    let loadingIndicator: HTMLElement | null = null
    const MIN_SUMMARIZE_LENGTH = 100 // Minimum characters for summarization
    
    // Save original text before any modifications
    const originalText = editorText
    
    if (!isWysiwyg && textarea && textarea.selectionStart !== textarea.selectionEnd) {
      // Summarize selection (only for source mode)
      const start = textarea.selectionStart
      const end = textarea.selectionEnd
      textToSummarize = editorText.substring(start, end)
      selection = { start, end, text: textToSummarize }
      
      // Check minimum length for selected text
      if (textToSummarize.trim().length < MIN_SUMMARIZE_LENGTH) {
        onStatusMessage(`요약하기 위해서는 최소 ${MIN_SUMMARIZE_LENGTH}자 이상의 텍스트가 필요합니다 (현재: ${textToSummarize.trim().length}자)`)
        return
      }
    } else {
      // Summarize all (for WYSIWYG or no selection)
      if (!editorText) {
        onStatusMessage('요약할 텍스트가 없습니다')
        return
      }
      // For full text, we don't enforce minimum length as strictly
      if (editorText.trim().length < MIN_SUMMARIZE_LENGTH / 2) {
        onStatusMessage('요약하기에는 텍스트가 너무 짧습니다')
        return
      }
    }
    
    aiFeaturesStore.setSelection(selection)
    aiFeaturesStore.setMode('summarize')
    
    // Show loading indicator (only for source mode)
    if (!isWysiwyg && textarea) {
      if (selection) {
        loadingIndicator = loadingIndicatorService.showLoadingIndicator(textarea, selection.end, 'summary')
      } else {
        loadingIndicator = loadingIndicatorService.showLoadingIndicator(textarea, editorText.length, 'summary')
      }
    }
    
    const modelName = getAbbreviatedModelName()
    const settings = settingsStore.get()
    const modelId = settings.ai?.selectedModel
    
    onStatusMessage(selection ? `선택 영역을 요약하고 있습니다... (${modelName})` : `전체 텍스트를 요약하고 있습니다... (${modelName})`, 'persist')
    
    try {
      aiFeaturesStore.setProcessing(true)
      
      // Use streaming for summary
      let accumulatedSummary = ''
      let summaryPrefix = ''
      let cursorHidden = false
      
      if (!selection) {
        // If summarizing entire text, prepare the prefix
        summaryPrefix = '\n\n---\n### 요약\n'
      }
      
      const response = await AIService.summaryStreaming(
        textToSummarize,
        (chunk) => {
          // Accumulate chunks
          accumulatedSummary += chunk
          
          // Update text in real-time
          if (isWysiwyg && tipTapEditor) {
            // For WYSIWYG mode, update TipTap editor directly
            const editor = (tipTapEditor as any).editor
            if (editor) {
              // Hide cursor during streaming if not already hidden
              if (!cursorHidden) {
                editor.commands.blur()
                cursorHidden = true
              }
              
              if (selection) {
                // Replace selection with accumulated summary (not common in WYSIWYG)
                const newText = originalText.substring(0, selection.start) + 
                              accumulatedSummary + 
                              originalText.substring(selection.end)
                editor.commands.setContent(newText)
              } else {
                // Append summary to the end
                const newText = originalText + summaryPrefix + accumulatedSummary
                editor.commands.setContent(newText)
                
                // Auto-scroll to the end for WYSIWYG mode (without focus to avoid cursor)
                requestAnimationFrame(() => {
                  // Find the scrollable wrapper element
                  const wrapper = document.querySelector('.tiptap-editor-wrapper')
                  if (wrapper) {
                    wrapper.scrollTop = wrapper.scrollHeight
                  }
                })
              }
            }
          } else {
            // For source mode
            if (selection) {
              const newText = originalText.substring(0, selection.start) + 
                            accumulatedSummary + 
                            originalText.substring(selection.end)
              onTextUpdate(newText, false) // false = don't add to history yet
            } else {
              // Append summary to the end of original text
              const newText = originalText + summaryPrefix + accumulatedSummary
              onTextUpdate(newText, false) // false = don't add to history yet
              
              // Auto-scroll to show the summary being generated
              // Wait for DOM update using Svelte's tick
              tick().then(() => {
                const currentTextarea = getTextarea()
                if (currentTextarea) {
                  // Keep cursor at the end during streaming
                  const currentLength = newText.length
                  currentTextarea.setSelectionRange(currentLength, currentLength)
                  // Scroll to bottom
                  scrollToBottom(currentTextarea)
                }
              })
            }
          }
        },
        modelId
      )
      
      if (response.success && response.result) {
        // Final update with history
        const summary = response.result as string
        
        if (isWysiwyg && tipTapEditor) {
          // For WYSIWYG mode
          const editor = (tipTapEditor as any).editor
          if (editor) {
            if (selection) {
              // Replace selection with summary
              const newText = originalText.substring(0, selection.start) + 
                            summary + 
                            originalText.substring(selection.end)
              editor.commands.setContent(newText)
              onTextUpdate(newText, true)
              onStatusMessage('선택 영역이 요약되었습니다')
            } else {
              // Append summary to the end
              const newText = originalText + summaryPrefix + summary
              editor.commands.setContent(newText)
              onTextUpdate(newText, true)
              onStatusMessage('요약이 추가되었습니다')
              
              // Final auto-scroll to show the complete summary
              setTimeout(() => {
                editor.commands.focus('end')
                // Find the scrollable wrapper element
                const wrapper = document.querySelector('.tiptap-editor-wrapper')
                if (wrapper) {
                  wrapper.scrollTop = wrapper.scrollHeight
                }
              }, 100)
            }
          }
        } else {
          // For source mode
          if (selection) {
            // Replace selection with summary
            const newText = originalText.substring(0, selection.start) + 
                          summary + 
                          originalText.substring(selection.end)
            
            undoRedoService.addEntry('ai-summarize', newText, correctionStates, {
              description: '선택 영역 요약 적용',
              previousText: originalText
            }, errorState.errors, hasChecked, errorState.ignoredErrors)
            
            onTextUpdate(newText, true)
            onStatusMessage('선택 영역이 요약되었습니다')
            
            // Focus and position cursor after the summary
            tick().then(() => {
              const currentTextarea = getTextarea()
              if (currentTextarea) {
                const summaryEnd = selection.start + summary.length
                currentTextarea.setSelectionRange(summaryEnd, summaryEnd)
                currentTextarea.focus()
              }
            })
          } else {
            // Append summary to the end
            const newText = originalText + summaryPrefix + summary
            
            undoRedoService.addEntry('ai-summarize', newText, correctionStates, {
              description: '전체 요약 추가',
              previousText: originalText
            }, errorState.errors, hasChecked, errorState.ignoredErrors)
            
            onTextUpdate(newText, true)
            onStatusMessage('요약이 추가되었습니다')
            
            // Auto-scroll to the bottom to show the new summary
            // Wait for DOM update using tick
            tick().then(() => {
              const currentTextarea = getTextarea()
              if (currentTextarea) {
                // Move cursor to the end and focus
                currentTextarea.setSelectionRange(newText.length, newText.length)
                currentTextarea.focus()
                // Scroll to bottom with aggressive retry
                scrollToBottom(currentTextarea)
                
                // Additional tick-based fallback
                tick().then(() => {
                  if (currentTextarea) {
                    currentTextarea.scrollTop = currentTextarea.scrollHeight
                  }
                })
              }
            })
          }
        }
      } else {
        // Restore original text on failure
        onTextUpdate(originalText, false)
        onStatusMessage('요약에 실패했습니다')
      }
    } finally {
      loadingIndicatorService.hideLoadingIndicator('summary')
      aiFeaturesStore.setProcessing(false)
      aiFeaturesStore.reset()
    }
  }
  
  export async function handleAIAnalysis() {
    // Check for YAML front matter
    const yamlInfo = removeYamlFrontMatter(editorText)
    
    // Check if text exists (excluding YAML)
    if (!yamlInfo.text || yamlInfo.text.trim() === '') {
      onStatusMessage('분석할 텍스트가 없습니다. 텍스트를 입력해주세요.', 3500, 'error')
      return
    }
    
    // Check if we're in split view already
    if (showSplitEditor) {
      onStatusMessage('이미 AI 분석 모드입니다', 3500, 'info')
      return
    }
    
    // Decide what to analyze based on existing errors
    if (errorState.errors.length > 0) {
      // If there are existing grammar errors, add AI comments to them
      // Adjust error positions if YAML is present
      let adjustedErrors = errorState.errors
      if (yamlInfo.hasYaml) {
        adjustedErrors = errorState.errors.filter(e => e.position.start >= yamlInfo.yamlOffset)
      }
      
      if (adjustedErrors.length === 0) {
        onStatusMessage('YAML 헤더 이후에 분석할 오류가 없습니다', 3500, 'info')
        return
      }
      
      onStatusMessage('맞춤법 오류를 AI로 분석합니다...')
      
      try {
        const { aiSuggestionsStore } = await import('$lib/stores/aiSuggestions')
        await aiSuggestionsStore.analyzeErrors(adjustedErrors, yamlInfo.text)
        onStatusMessage('AI 분석이 완료되었습니다')
        hasChecked = true
      } catch (error) {
        console.error('AI error analysis failed:', error)
        onStatusMessage('AI 분석 중 오류가 발생했습니다', 3500, 'error')
      }
    } else {
      // No existing errors - let AI find errors directly
      onStatusMessage('AI로 텍스트를 분석합니다...')
      
      try {
        const { aiSuggestionsStore } = await import('$lib/stores/aiSuggestions')
        // Store the AI analysis service instance for cancellation
        aiAnalysisService = aiSuggestionsStore.getAIAnalysisService?.() || null
        const errors = await aiSuggestionsStore.analyzeTextDirectly(yamlInfo.text)
        
        if (errors && errors.length > 0) {
          hasChecked = true
          onStatusMessage(`AI가 ${errors.length}개의 개선점을 발견했습니다`)
          
          // Force refresh of error display
          const { grammarActions } = await import('$lib/stores/grammar')
          // Set errors with AI merge enabled to ensure they show up
          grammarActions.setErrors(errors, true)
        } else {
          hasChecked = true
          onStatusMessage('AI 분석 결과 문제가 없습니다')
        }
      } catch (error) {
        console.error('AI text analysis failed:', error)
        onStatusMessage('AI 분석 중 오류가 발생했습니다', 3500, 'error')
      }
    }
  }
  
  export async function handleCritic() {
    onStatusMessage('평가받기 기능은 준비중입니다')
    // TODO: Implement critic logic
  }
  
  /**
   * Get the AI analysis service instance for cancellation
   */
  export function getAIAnalysisService() {
    return aiAnalysisService
  }
  
  // Helper function to save AI segments state for undo/redo
  function saveAISegmentsState(description: string) {
    // Save current state with deep clone of segments
    const segmentsClone = aiSegments && aiSegments.length > 0 
      ? aiSegments.map(s => ({ 
          ...s,
          status: s.status,
          selected: s.selected,
          suggested: s.suggested,
          hasChanges: s.hasChanges,
          original: s.original,
          reason: s.reason,
          mode: s.mode,
          history: s.history ? [...s.history] : [],
          historyIndex: s.historyIndex
        })) 
      : undefined
    
    // Save AI segments state
    undoRedoService.addToHistory({
      text: editorText,
      cursorPosition: 0,
      description,
      aiSegments: segmentsClone
    })
  }
  
  export function hideAllAILoadingIndicators() {
    loadingIndicatorService.hideAllLoadingIndicators()
  }
  
  // Export segments state
  export function getAISegments() {
    return aiSegments
  }
  
  export function setAISegments(segments: any[]) {
    aiSegments = segments
  }
  
  export function resetAIState() {
    hasInitialAIState = false
    aiSegments = []
    aiFeaturesStore.reset()
    aiFeaturesStore.setProcessing(false)

    // Also reset AI service analyzing state
    aiServiceActions.forceResetAnalyzing()
  }
</script>